  (function() {
    if (customElements.get('susu-auto-faq-init')) return;

    customElements.define('susu-auto-faq-init', class extends HTMLElement {
      connectedCallback() {
        this.start();
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
      }

      start() {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        const root = document.getElementById(this.dataset.rootId);
        if (!root) return;
        const input = root.querySelector('[data-faq-search]');
        const clearButton = root.querySelector('[data-faq-clear]');
        const filters = Array.from(root.querySelectorAll('[data-faq-filters] button'));
        const list = root.querySelector('[data-faq-list]');
        const emptyState = root.querySelector('[data-faq-empty]');
        const initialEmpty = root.querySelector('[data-faq-initial-empty]');
        const status = root.querySelector('[data-faq-status]');
        const retryButton = root.querySelector('[data-faq-retry]');
        const moreButton = root.querySelector('[data-faq-more]');
        const schema = root.querySelector('[data-faq-schema]');
        if (!list || !input || !schema) return;

        const records = new Map();
        const searchText = new WeakMap();
        const pageCount = Math.max(1, Number(root.dataset.pageCount) || 1);
        const currentPage = Number(root.dataset.currentPage) || 1;
        const loadedPages = new Set([currentPage]);
        let items = [];
        let activeCategory = 'all';
        let loading = false;
        let failed = false;
        let openedFirst = false;

        function normalize(value) {
          return (value || '').toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        }

        function addItems(container) {
          container.querySelectorAll('[data-faq-item]').forEach(function(item) {
            const key = item.dataset.faqKey;
            if (key && !records.has(key)) {
              records.set(key, item);
              searchText.set(item, normalize(item.textContent + ' ' + item.dataset.faqCategory + ' ' + (item.dataset.faqKeywords || item.dataset.faqSearchText || '')));
            }
          });
        }

        function updateResults() {
          const query = normalize(input.value.trim());
          let visibleCount = 0;
          items.forEach(function(item) {
            const matchesCategory = activeCategory === 'all' || item.dataset.faqCategory === activeCategory;
            const matchesQuery = !query || searchText.get(item).includes(query);
            item.hidden = !(matchesCategory && matchesQuery);
            if (item.hidden) item.open = false;
            else visibleCount += 1;
          });
          clearButton.hidden = !query;
          const complete = loadedPages.size === pageCount;
          root.dataset.faqComplete = String(complete);
          initialEmpty.hidden = items.length > 0 || !complete;
          emptyState.hidden = items.length === 0 || visibleCount > 0 || !complete;
          const countText = visibleCount === 1
            ? (root.dataset.resultSingular || '1 question')
            : (root.dataset.resultPlural || '[count] questions').replace('[count]', visibleCount);
          const partialText = (root.dataset.partialLabel || 'Loaded: [count]').replace('[count]', visibleCount);
          status.textContent = loading
            ? root.dataset.loadingLabel
            : failed ? partialText + '. ' + root.dataset.loadErrorLabel : complete ? countText : partialText;
          retryButton.hidden = !failed || loading;
          if (moreButton) {
            moreButton.hidden = complete || failed;
            moreButton.disabled = loading;
          }
          root.setAttribute('aria-busy', String(loading));
        }

        function renderItems() {
          function order(item) {
            const value = item.dataset.faqSort.trim();
            return value !== '' && Number.isFinite(Number(value)) ? Number(value) : Infinity;
          }
          items = Array.from(records.values()).sort(function(a, b) {
            const difference = order(a) - order(b);
            return difference || a.dataset.faqKey.localeCompare(b.dataset.faqKey);
          });
          const fragment = document.createDocumentFragment();
          items.forEach(function(item) { fragment.appendChild(item); });
          list.replaceChildren(fragment);
          if (items.length && !openedFirst) {
            openedFirst = true;
            if (root.dataset.openFirst === 'true' && items[0]) items[0].open = true;
          }
          schema.type = items.length && root.dataset.faqSchemaEnabled === 'true' ? 'application/ld+json' : 'application/json';
          schema.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            '@id': root.dataset.faqSchemaId,
            isPartOf: { '@id': root.dataset.faqPageId },
            inLanguage: root.dataset.faqLanguage,
            mainEntity: items.map(function(item) {
              return {
                '@type': 'Question',
                name: item.querySelector('summary').textContent.trim(),
                acceptedAnswer: { '@type': 'Answer', text: item.querySelector('.faq-directory__answer').textContent.trim() }
              };
            })
          }).replace(/\u003c/g, '\\u003c');
          updateResults();
        }

        async function fetchPage(pageNumber) {
          const url = new URL(window.location.href);
          url.hash = '';
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', root.dataset.sectionId);
          url.searchParams.set(root.dataset.pageParam || 'page', String(pageNumber));
          const requestController = new AbortController();
          const abortRequest = function() { requestController.abort(); };
          signal.addEventListener('abort', abortRequest, { once: true });
          const timeout = setTimeout(abortRequest, 15000);
          try {
            const response = await fetch(url.toString(), { signal: requestController.signal, credentials: 'same-origin' });
            if (!response.ok) throw new Error('FAQ page request failed');
            const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
            const nextRoot = documentFragment.getElementById(root.id);
            if (!nextRoot || Number(nextRoot.dataset.currentPage) !== pageNumber || Number(nextRoot.dataset.pageCount) !== pageCount) {
              throw new Error('FAQ page response did not match');
            }
            if (signal.aborted) return;
            addItems(nextRoot);
            loadedPages.add(pageNumber);
          } finally {
            clearTimeout(timeout);
            signal.removeEventListener('abort', abortRequest);
          }
        }

        async function loadRemaining(loadAll) {
          if (loading || signal.aborted) return;
          const pending = [];
          for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
            if (!loadedPages.has(pageNumber)) pending.push(pageNumber);
          }
          if (!pending.length) return;
          loading = true;
          failed = false;
          updateResults();
          let cursor = 0;
          async function worker() {
            while (cursor < pending.length && !signal.aborted) {
              const pageNumber = pending[cursor++];
              try { await fetchPage(pageNumber); }
              catch (error) { if (!signal.aborted) failed = true; }
              if (!loadAll && (records.size > 0 || failed)) break;
            }
          }
          // Browsing requests one page; explicit searches inspect all public FAQ pages.
          if (loadAll) await Promise.all([worker(), worker()]);
          else await worker();
          if (signal.aborted) return;
          loading = false;
          renderItems();
          if (!loadAll && !failed && (input.value.trim() || activeCategory !== 'all')) loadRemaining(true);
        }

        let searchTimer;
        signal.addEventListener('abort', function() { clearTimeout(searchTimer); }, { once: true });
        input.addEventListener('input', function() {
          clearTimeout(searchTimer);
          searchTimer = setTimeout(function() {
            updateResults();
            if (input.value.trim() || activeCategory !== 'all') loadRemaining(true);
          }, 150);
        }, { signal: signal });
        clearButton.addEventListener('click', function() {
          input.value = '';
          input.focus();
          updateResults();
        }, { signal: signal });
        filters.forEach(function(filter) {
          filter.addEventListener('click', function() {
            activeCategory = filter.dataset.faqCategory;
            filters.forEach(function(button) {
              const active = button === filter;
              button.classList.toggle('is-active', active);
              button.setAttribute('aria-pressed', String(active));
            });
            updateResults();
            if (activeCategory !== 'all' || input.value.trim()) loadRemaining(true);
          }, { signal: signal });
        });
        retryButton.addEventListener('click', function() {
          loadRemaining(Boolean(input.value.trim()) || activeCategory !== 'all');
        }, { signal: signal });
        if (moreButton) moreButton.addEventListener('click', function() { loadRemaining(false); }, { signal: signal });
        addItems(root);
        root.dataset.faqReady = 'true';
        renderItems();
        if (!records.size && pageCount > 1) loadRemaining(false);
      }
    });
  })();
